import React from "react";
import { Stack, Typography, Box, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import BusinessIcon from "@mui/icons-material/Business";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createBranch, updateBranch } from "../redux/actions/branchActions";
import { cleanFormData } from "../helpers/formHelpers";

import { BranchResponseDto } from "../shared/dtos/branch/BranchResponseDto";
import { branchActions } from "../redux/slices/branch";

interface BranchFormValues {
  name: string;
  branchCode: string;
  licenseKey: string | null;
  contactEmail: string;
  contactPhone: string;
  address: {
    streetLine1: string;
    city: string;
    stateOrProvince: string;
    postalCode: string;
    country: string;
  };
}

const addressSchema: Yup.ObjectSchema<BranchFormValues["address"]> =
  Yup.object().shape({
    streetLine1: Yup.string().required("Required"),
    city: Yup.string().required("Required"),
    stateOrProvince: Yup.string().required("Required"),
    postalCode: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
  });

const getBranchSchema = (
  isEditing: boolean,
): Yup.ObjectSchema<BranchFormValues> =>
  Yup.object().shape({
    name: Yup.string().required("Required"),
    branchCode: Yup.string().required("Required"),
    licenseKey: isEditing
      ? Yup.string().nullable()
      : Yup.string().required("Required"),
    contactEmail: Yup.string().email("Invalid email").required("Required"),
    contactPhone: Yup.string().required("Required"),
    address: addressSchema,
  }) as Yup.ObjectSchema<BranchFormValues>;

interface CreateOrUpdateBranchFormProps {
  branch?: BranchResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateBranchForm: React.FC<CreateOrUpdateBranchFormProps> = ({
  branch = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { company } = useAppSelector((state) => state.company);

  const initialValues = {
    companyId: company?.id || "",
    name: branch?.name || "",
    branchCode: branch?.branchCode || "",
    licenseKey: "",
    contactEmail: branch?.contactEmail || "",
    contactPhone: branch?.contactPhone || "",
    address: {
      streetLine1: branch?.address?.streetLine1 || "",
      streetLine2: branch?.address?.streetLine2 || "",
      city: branch?.address?.city || "",
      stateOrProvince: branch?.address?.stateOrProvince || "",
      postalCode: branch?.address?.postalCode || "",
      country: branch?.address?.country || "",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(branchActions.setErrorUpdate(null))
            : () => dispatch(branchActions.setErrorCreate(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getBranchSchema(isEditing)}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values);
          if (isEditing && branch) {
            dispatch(updateBranch({ id: branch.id, values: cleanedValues }));
          } else {
            dispatch(createBranch(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={4}>
              {/* --- BRANCH INFO --- */}
              <Box>
                <Typography sx={{ mb: 2, fontWeight: 700, fontSize: "1.1rem" }}>
                  Branch Details
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  }}
                >
                  <FormTextField name="name" label="Branch Name" />
                  <FormTextField name="branchCode" label="Code" />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: isEditing ? "1fr 1fr" : "1fr 1fr 1fr",
                    },
                  }}
                >
                  {!isEditing && (
                    <FormTextField name="licenseKey" label="License Key" />
                  )}
                  <FormTextField
                    name="contactEmail"
                    label="Email"
                    type="email"
                  />
                  <FormTextField name="contactPhone" label="Phone" />
                </Box>
              </Box>

              {/* --- ADDRESS SECTION --- */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.5),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: 1,
                  }}
                >
                  Location Information
                </Typography>

                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <FormTextField
                      name="address.streetLine1"
                      label="Street Line 1"
                    />
                    <FormTextField
                      name="address.streetLine2"
                      label="Street Line 2"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <FormTextField name="address.city" label="City" />
                    <FormTextField
                      name="address.stateOrProvince"
                      label="State/Province"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <FormTextField
                      name="address.postalCode"
                      label="Postal Code"
                    />
                    <FormTextField name="address.country" label="Country" />
                  </Box>
                </Stack>
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Branch"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={isEditing ? <SaveOutlinedIcon /> : <BusinessIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default CreateOrUpdateBranchForm;
